/**
 * Restaurant Detail Page - Todo en Local
 * Uber Eats-style restaurant page with section-based menu and sticky tabs
 */

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Star, Clock, MapPin, Truck, Phone, ArrowLeft, Utensils, ShoppingCart, Plus, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { MarketplaceHeader, MarketplaceFooter, FloatingCartButton, MobileBottomNav } from '../../components/marketplace/MarketplaceLayout';
import FoodProductSheet from '../../components/marketplace/FoodProductSheet';
import api from '../../services/api';
import { toast } from 'sonner';

const RestaurantDetail = () => {
  const { businessId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const sectionRefs = useRef({});

  useEffect(() => {
    loadRestaurant();
  }, [businessId]);

  const loadRestaurant = async () => {
    try {
      const lat = localStorage.getItem('user_lat');
      const lng = localStorage.getItem('user_lng');
      const params = {};
      if (lat && lng) { params.lat = lat; params.lng = lng; }
      const res = await api.get(`/comida/restaurants/${businessId}`, { params });
      setData(res.data);
      if (res.data.menu_sections?.length > 0) {
        setActiveSection(res.data.menu_sections[0].section_id);
      }
    } catch (e) {
      console.error('Error loading restaurant:', e);
      toast.error('Error al cargar restaurante');
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    const el = sectionRefs.current[sectionId];
    if (el) {
      const offset = 160; // header + tabs height
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MarketplaceHeader />
        <div className="flex justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gray-50">
        <MarketplaceHeader />
        <div className="text-center py-32">
          <p className="text-gray-500 text-lg">Restaurante no encontrado</p>
        </div>
      </div>
    );
  }

  const { restaurant: r, menu_sections } = data;
  const pageTitle = `${r.name} | Menú y Pedidos a Domicilio - enLocal Comida`;
  const pageDesc = `Pide de ${r.name} en Puerto Vallarta. ${r.cuisine_tags?.join(', ') || 'Comida deliciosa'}. ${r.prep_time} min de preparación. Entrega a domicilio.`;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={r.cover_image_url || r.logo_url || '/logo.png'} />
        <meta property="og:url" content={`https://todoenlocal.com/comida/restaurante/${businessId}`} />
        <meta property="og:type" content="restaurant" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
      </Helmet>
      <MarketplaceHeader />

      {/* Cover / Hero */}
      <div className="relative">
        {r.cover_image_url || r.logo_url ? (
          <img src={r.cover_image_url || r.logo_url} alt={r.name} className="w-full h-48 md:h-64 object-cover" />
        ) : (
          <div className="w-full h-48 md:h-64 bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center">
            <Utensils className="w-20 h-20 text-white/30" />
          </div>
        )}
        <button
          onClick={() => navigate('/comida')}
          className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm p-2 rounded-full shadow-lg hover:bg-white transition"
        >
          <ArrowLeft className="w-5 h-5 text-gray-700" />
        </button>
        {!r.is_open && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
            <span className="bg-red-600 text-white px-6 py-2 rounded-full text-lg font-bold">Cerrado</span>
          </div>
        )}
      </div>

      {/* Restaurant Info */}
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white -mt-8 relative z-10 rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900">{r.name}</h1>
              {r.cuisine_tags?.length > 0 && (
                <p className="text-gray-500 mt-1">{r.cuisine_tags.join(' · ')}</p>
              )}
            </div>
            {r.logo_url && (
              <img src={r.logo_url} alt="" className="w-14 h-14 rounded-xl object-cover border-2 border-white shadow" />
            )}
          </div>

          <div className="flex flex-wrap items-center gap-4 mt-4 text-sm">
            {r.rating && (
              <span className="flex items-center gap-1.5 bg-yellow-50 text-yellow-700 px-3 py-1.5 rounded-full font-medium">
                <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
                {r.rating} ({r.review_count} opiniones)
              </span>
            )}
            <span className="flex items-center gap-1.5 text-gray-600">
              <Clock className="w-4 h-4 text-orange-500" />
              {r.prep_time} min
            </span>
            {r.distance_km != null && (
              <span className="flex items-center gap-1.5 text-gray-600">
                <MapPin className="w-4 h-4 text-orange-500" />
                {r.distance_km} km
              </span>
            )}
            {r.delivery_enabled && (
              <span className="flex items-center gap-1.5 text-green-700 bg-green-50 px-3 py-1.5 rounded-full">
                <Truck className="w-4 h-4" />
                Envío ${r.delivery_fee || 0}
              </span>
            )}
            {r.phone && (
              <a href={`tel:${r.phone}`} className="flex items-center gap-1.5 text-gray-600 hover:text-orange-600">
                <Phone className="w-4 h-4" />
                {r.phone}
              </a>
            )}
          </div>

          <p className="text-sm text-gray-500 mt-3 flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" /> {r.address}
          </p>
        </div>

        {/* Sticky Section Tabs */}
        {menu_sections.length > 0 && (
          <div className="sticky top-16 z-20 bg-gray-50 pt-4 pb-2">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-1 flex gap-1 overflow-x-auto scrollbar-hide">
              {menu_sections.map((section) => (
                <button
                  key={section.section_id}
                  onClick={() => scrollToSection(section.section_id)}
                  className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                    activeSection === section.section_id
                      ? 'bg-orange-600 text-white shadow-sm'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {section.section_name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Menu Sections */}
        <div className="space-y-8 py-6 pb-32">
          {menu_sections.map((section) => (
            <div
              key={section.section_id}
              ref={(el) => { sectionRefs.current[section.section_id] = el; }}
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">{section.section_name}</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {section.products.map((product) => (
                  <button
                    key={product.id}
                    onClick={() => setSelectedProduct(product)}
                    className="flex bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-md transition-all text-left group w-full"
                  >
                    <div className="flex-1 p-4">
                      <h3 className="font-semibold text-gray-900 group-hover:text-orange-600 transition-colors">{product.name}</h3>
                      {product.description_short && (
                        <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.description_short}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-lg font-bold text-gray-900">
                          ${(product.is_combo ? product.combo_price : product.pricing?.retail_price || 0).toFixed(0)}
                        </span>
                        {product.is_combo && (
                          <span className="text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-medium">Combo</span>
                        )}
                        {product.modifier_groups?.length > 0 && (
                          <span className="text-xs text-gray-400">Personalizable</span>
                        )}
                      </div>
                    </div>
                    {(product.image_url || product.thumbnail_url) ? (
                      <div className="relative w-28 h-28 flex-shrink-0">
                        <img src={product.thumbnail_url || product.image_url} alt="" className="w-full h-full object-cover" />
                        <div className="absolute bottom-2 right-2 bg-orange-600 text-white p-1 rounded-full shadow">
                          <Plus className="w-4 h-4" />
                        </div>
                      </div>
                    ) : (
                      <div className="w-28 h-28 flex-shrink-0 bg-gradient-to-br from-orange-50 to-red-50 flex items-center justify-center">
                        <Plus className="w-6 h-6 text-orange-400" />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Product Detail Sheet */}
      {selectedProduct && (
        <FoodProductSheet
          product={selectedProduct}
          businessId={businessId}
          branchId={r.branch_id}
          onClose={() => setSelectedProduct(null)}
        />
      )}

      <MarketplaceFooter />
      <FloatingCartButton />
      <MobileBottomNav />
    </div>
  );
};

export default RestaurantDetail;
